// Article « Rétroplanning mariage » (référencement). En français : il vise les
// recherches Google françaises. Le détail mois par mois vient du catalogue du
// rétroplanning de l'app (components/admin/RetroPanel/catalog.ts) : article et
// outil proposent les mêmes étapes, aux mêmes échéances.

export const UPDATED = '2026-10-15';
export const READING_MINUTES = 9;

export const TOC = [
  { id: 'definition', label: 'Qu’est-ce qu’un rétroplanning de mariage ?' },
  { id: 'quand-commencer', label: 'Quand commencer à organiser son mariage ?' },
  { id: 'resume', label: 'Le rétroplanning en résumé' },
  { id: 'detail', label: 'Le rétroplanning détaillé, mois par mois' },
  { id: 'express', label: 'Mariage dans 6 mois ou moins : le rétroplanning express' },
  { id: 'demarches', label: 'Les démarches administratives' },
  { id: 'erreurs', label: 'Les 6 erreurs qui font déraper un rétroplanning' },
  { id: 'faq', label: 'Questions fréquentes' },
];

export const MILESTONES = [
  {
    when: '12 à 18 mois avant',
    title: 'Les fondations',
    text: 'Date, budget, première liste d’invités, puis le lieu : c’est lui qui décide du reste. Enchaînez avec le traiteur et le photographe.',
  },
  {
    when: '9 à 12 mois avant',
    title: 'Les grands prestataires',
    text: 'Robe et costume, DJ ou musiciens, officiant ou paroisse. Les save the date partent pour que vos invités bloquent le jour.',
  },
  {
    when: '6 à 9 mois avant',
    title: 'L’ambiance et l’accueil',
    text: 'Fleuriste et décoration, hébergements pour les invités qui viennent de loin, liste de mariage.',
  },
  {
    when: '4 à 6 mois avant',
    title: 'Les faire-part',
    text: 'Commande puis envoi des faire-part, choix des alliances, transport des mariés et des invités.',
  },
  {
    when: '1 à 4 mois avant',
    title: 'Les essais et les démarches',
    text: 'Essais coiffure et maquillage, dégustation du menu, musiques des temps forts, dossier à la mairie.',
  },
  {
    when: 'Le dernier mois',
    title: 'Les derniers réglages',
    text: 'Relances des invités, plan de table, déroulé du jour J, confirmations aux prestataires.',
  },
  {
    when: 'La veille',
    title: 'Souffler',
    text: 'Les alliances aux témoins, les soldes prêts, et du repos : tout est prêt.',
  },
];

// Introduction de chaque période du détail (clé de période du rétroplanning sur 18 mois)
export const PERIOD_INTRO: Record<string, string> = {
  p18: 'Avant de réserver quoi que ce soit, mettez-vous d’accord sur trois chiffres : la date (ou une saison), le budget et le nombre d’invités. Ils décident du lieu, et le lieu décide du reste.',
  p16: 'Le lieu est réservé ? Prenez le temps de répartir le budget poste par poste avant de signer avec les autres prestataires.',
  p14: 'Les prestataires les plus demandés se réservent maintenant, surtout pour un mariage entre mai et septembre. Demandez plusieurs devis et lisez les conditions d’acompte.',
  p12: 'Place aux tenues (comptez plusieurs mois de confection pour une robe sur mesure) et à la musique. Pour une cérémonie religieuse ou laïque, c’est le moment de rencontrer la paroisse ou l’officiant.',
  p10: 'Vos invités doivent pouvoir bloquer la date : les save the date partent. Pensez aussi à ceux qui viennent de loin.',
  p8: 'La liste de mariage peut figurer dès le faire-part : créez-la avant de le commander.',
  p6: 'Les faire-part partent, avec une date limite de réponse environ un mois avant le mariage. Les alliances et le transport se réservent.',
  p4: 'La période des essais, de la dégustation et des démarches. Pour le mariage civil, déposez le dossier à la mairie dans les délais qu’elle vous indique.',
  m2: 'Les réponses arrivent : relancez ceux qui n’ont pas répondu, vous en aurez besoin pour le traiteur et le plan de table.',
  m1: 'Le mois des derniers grands chantiers : plan de table, déroulé de la journée, rôle de chacun.',
  w2: 'Tout se confirme : horaires, accès, nombre de couverts, menus.',
  w1: 'Les dernières impressions et les derniers rendez-vous.',
  d1: 'Plus rien à organiser. Confiez ce qui doit l’être, et reposez-vous.',
};

export const EXPRESS = [
  {
    title: 'Fixez date, budget et nombre d’invités',
    text: 'Le jour même. Sans ces trois chiffres, impossible de demander un devis.',
  },
  {
    title: 'Réservez le lieu et le traiteur en premier',
    text: 'Ce sont les plus longs à trouver. Un lieu avec traiteur attitré vous fait gagner des semaines.',
  },
  {
    title: 'Envoyez une invitation sans attendre',
    text: 'Pas le temps de passer par des save the date : une invitation en ligne, avec réponse directe, part le jour où le lieu est confirmé.',
  },
  {
    title: 'Choisissez des tenues disponibles rapidement',
    text: 'Prêt-à-porter, collections de créateurs disponibles en boutique, ou retouches d’une robe existante plutôt que du sur-mesure.',
  },
  {
    title: 'Lancez les démarches tout de suite',
    text: 'Contactez la mairie dès la date fixée : certaines demandent le dossier plusieurs semaines à l’avance.',
  },
  {
    title: 'Simplifiez le reste',
    text: 'Une seule réception plutôt que plusieurs moments, une déco sobre, une playlist plutôt qu’un orchestre : gardez votre énergie pour l’essentiel.',
  },
];

export const ADMIN = [
  {
    title: 'Mariage civil',
    text: 'Le dossier se dépose à la mairie de la commune où l’un de vous (ou l’un de vos parents) a son domicile ou sa résidence. Comptez actes de naissance récents, justificatifs d’identité et de domicile, et les informations sur vos témoins (deux à quatre). La publication des bans est affichée en mairie dix jours avant la célébration. Chaque mairie fixe ses délais : renseignez-vous dès que la date est connue.',
  },
  {
    title: 'Contrat de mariage',
    text: 'Facultatif. S’il y en a un, il se signe chez le notaire avant le mariage, qui vous remet un certificat à donner à la mairie.',
  },
  {
    title: 'Cérémonie religieuse',
    text: 'En France, elle ne peut avoir lieu qu’après le mariage civil. Prenez contact avec la paroisse tôt : la préparation au mariage s’étale souvent sur plusieurs mois.',
  },
  {
    title: 'Cérémonie laïque',
    text: 'Elle n’a pas de valeur légale, mais demande de la préparation : officiant, déroulé, textes et vœux, à écrire deux à quatre mois avant.',
  },
];

export const MISTAKES = [
  {
    title: 'Réserver des prestataires avant le lieu',
    text: 'Le lieu fixe la date, la capacité et parfois le traiteur. Tout réserver avant, c’est risquer de tout déplacer.',
  },
  {
    title: 'Oublier les imprévus dans le budget',
    text: 'Gardez une marge de 5 à 10 % : heures supplémentaires, invités en plus, retouches de dernière minute.',
  },
  {
    title: 'Envoyer les faire-part trop tard',
    text: 'Moins de trois mois avant, une partie des invités a déjà d’autres projets, surtout l’été.',
  },
  {
    title: 'Ne pas fixer de date limite de réponse',
    text: 'Sans elle, les réponses traînent jusqu’à la dernière semaine, et le traiteur attend ses chiffres.',
  },
  {
    title: 'Faire le plan de table trop tôt',
    text: 'Commencez-le quand la majorité a répondu, mais ne le figez que deux à trois semaines avant.',
  },
  {
    title: 'Tout porter à deux',
    text: 'Témoins, familles, amis proches : confiez-leur des tâches précises (accueil, discours, animation) plutôt que de tout garder.',
  },
];

export const FAQ = [
  {
    q: 'Qu’est-ce qu’un rétroplanning de mariage ?',
    a: 'C’est la liste des étapes de l’organisation, classées à rebours depuis la date du mariage : ce qu’il faut faire 12 mois avant, 6 mois avant, 1 mois avant, jusqu’à la veille.',
  },
  {
    q: 'Quand commencer à préparer son mariage ?',
    a: 'En général 12 à 18 mois avant. Les lieux de réception et les prestataires les plus demandés se réservent souvent plus d’un an à l’avance, surtout de mai à septembre.',
  },
  {
    q: 'Peut-on organiser un mariage en 6 mois, ou moins ?',
    a: 'Oui, en réservant d’abord le lieu et le traiteur, en invitant tout de suite, et en simplifiant le reste. Un rétroplanning calé sur votre date resserre les étapes sur le temps qu’il vous reste.',
  },
  {
    q: 'Quand envoyer les save the date et les faire-part ?',
    a: 'Les save the date 6 à 10 mois avant, pour que vos invités bloquent la date. Les faire-part 4 à 6 mois avant, avec une date limite de réponse environ un mois avant le mariage.',
  },
  {
    q: 'Quand déposer le dossier de mariage à la mairie ?',
    a: 'Chaque mairie fixe ses délais, souvent un à trois mois avant la date, parfois plus. Renseignez-vous dès que la date est connue. La publication des bans dure ensuite dix jours.',
  },
  {
    q: 'Rétroplanning Excel ou en ligne ?',
    a: 'Un tableur ne se met pas à jour tout seul. En ligne, les étapes sont datées d’après votre date, se cochent à deux, et sont reliées à vos invités, votre budget et votre plan de table.',
  },
];
